/**
 * Gate a route on one facet of Ops (ADR-0060).
 *
 * Deliberately **not** `RequirePlatformStaff`. Configuration authority is a facet, and the
 * platform-staff flag is ADR-0015's cross-tenant bypass — an operator who holds `policy`
 * without the bypass is exactly who the configuration screens are for, and wrapping them in
 * the staff guard would send that person home from the one screen they were granted.
 *
 * A redirect rather than a refusal message, on `RequirePlatformStaff`'s reasoning. The API
 * refuses independently with `ops_facet_required`, so this is a navigation courtesy and never
 * the control itself. *
 * **It says why now (ADR-0085), for an Ops user and not for a Merchant one.** The redirect
 * above was the whole answer, so an operator following a colleague's link arrived at the
 * landing page with the URL gone from the address bar and nothing to go on. A Merchant user
 * still goes home, because that reasoning holds for them: this is not their screen and their
 * own is one route away. An Ops user who lacks one responsibility is told which. The sentence is `refusal.needsFacet`, which is
 * the one `i18n/refusal.ts` already renders for the API's own `facet` detail — so a reader
 * meets one wording whether they were refused at the door or after the request.
 */

import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSession } from './session.js';
import { AuthorityRefused } from './authority-refused.js';

export function RequireOpsFacet({
  facet,
  children,
}: {
  readonly facet: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  const { state } = useSession();

  if (state.status !== 'authenticated') return <>{children}</>;
  if (state.session.role !== 'opsUser') return <Navigate to="/" replace />;
  if (!state.session.opsFacets.includes(facet)) {
    return <AuthorityRefused needs={{ kind: 'facet', facet }} />;
  }

  return <>{children}</>;
}
